import { useState, useEffect, useCallback } from 'react';
import apiClient from '../api';
import { SKU, SKUFormData, SKUListResponse, SKUActionResponse } from '../types/sku';

interface UseSkusReturn {
  skus: SKU[];
  loading: boolean;
  error: string | null;
  totalSkus: number;
  activeSkus: number;
  inactiveSkus: number;
  addSku: (data: SKUFormData) => Promise<void>;
  updateSku: (skuId: string, data: Partial<SKUFormData>) => Promise<void>;
  deleteSku: (skuId: string) => Promise<void>;
  toggleSku: (skuId: string) => Promise<void>;
  refresh: () => Promise<void>;
  clearError: () => void;
}

export function useSkus(appName: string | null): UseSkusReturn {
  const [skus, setSkus] = useState<SKU[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });

  const fetchSkus = useCallback(async () => {
    if (!appName) {
      setSkus([]);
      setStats({ total: 0, active: 0, inactive: 0 });
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get<SKUListResponse>(
        `/adjust-config/${encodeURIComponent(appName)}/skus`
      );
      // API returns { success: true, result: { skus: [...], totalSkus, activeSkus, inactiveSkus } }
      const skuData = data.result || data.data;
      if (data.success && skuData) {
        setSkus(skuData.skus || []);
        setStats({
          total: skuData.totalSkus || 0,
          active: skuData.activeSkus || 0,
          inactive: skuData.inactiveSkus || 0,
        });
      } else {
        setError('Failed to fetch SKUs');
      }
    } catch (e: any) {
      const message = e.response?.data?.message || 'Failed to fetch SKUs';
      setError(message);
      setSkus([]);
      setStats({ total: 0, active: 0, inactive: 0 });
    } finally {
      setLoading(false);
    }
  }, [appName]);

  useEffect(() => {
    fetchSkus();
  }, [fetchSkus]);

  const handleAddSku = async (data: SKUFormData) => {
    if (!appName) throw new Error('No app selected');

    const response = await apiClient.post<SKUActionResponse>(
      `/adjust-config/${encodeURIComponent(appName)}/skus`,
      data
    );

    if (response.data.success) {
      await fetchSkus();
    } else {
      throw new Error(response.data.message || 'Failed to add SKU');
    }
  };

  const handleUpdateSku = async (skuId: string, data: Partial<SKUFormData>) => {
    if (!appName) throw new Error('No app selected');

    const response = await apiClient.put<SKUActionResponse>(
      `/adjust-config/${encodeURIComponent(appName)}/skus/${encodeURIComponent(skuId)}`,
      data
    );

    if (response.data.success) {
      await fetchSkus();
    } else {
      throw new Error(response.data.message || 'Failed to update SKU');
    }
  };

  const handleDeleteSku = async (skuId: string) => {
    if (!appName) throw new Error('No app selected');

    const response = await apiClient.delete<SKUActionResponse>(
      `/adjust-config/${encodeURIComponent(appName)}/skus/${encodeURIComponent(skuId)}`
    );

    if (response.data.success) {
      await fetchSkus();
    } else {
      throw new Error(response.data.message || 'Failed to delete SKU');
    }
  };

  const handleToggleSku = async (skuId: string) => {
    if (!appName) throw new Error('No app selected');

    const response = await apiClient.patch<SKUActionResponse>(
      `/adjust-config/${encodeURIComponent(appName)}/skus/${encodeURIComponent(skuId)}/toggle`
    );

    if (response.data.success) {
      await fetchSkus();
    } else {
      throw new Error(response.data.message || 'Failed to toggle SKU status');
    }
  };

  const clearError = () => setError(null);

  return {
    skus,
    loading,
    error,
    totalSkus: stats.total,
    activeSkus: stats.active,
    inactiveSkus: stats.inactive,
    addSku: handleAddSku,
    updateSku: handleUpdateSku,
    deleteSku: handleDeleteSku,
    toggleSku: handleToggleSku,
    refresh: fetchSkus,
    clearError,
  };
}
